import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { store } from "@/store";
import { theme } from "@/theme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAllData } from "@/store/slices/dataSlice";
import { hideSnackbar } from "@/store/slices/uiSlice";
import Shell from "@/components/templates/Shell";
import { APP_ROUTES } from "@/constants/routes";
import ErrorBoundary from "@/components/molecules/ErrorBoundary";


const Login = lazy(() => import("@/pages/Login/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard/Dashboard"));
const Chat = lazy(() => import("@/pages/Chat/Chat"));
const Employees = lazy(() => import("@/pages/Employees/Employees"));
const Attendance = lazy(() => import("@/pages/Attendance/Attendance"));
const Leave = lazy(() => import("@/pages/Leave/Leave"));
const Payroll = lazy(() => import("@/pages/Payroll/Payroll"));
const Recruitment = lazy(() => import("@/pages/Recruitment/Recruitment"));
const Onboarding = lazy(() => import("@/pages/Onboarding/Onboarding"));
const Performance = lazy(() => import("@/pages/Performance/Performance"));
const Training = lazy(() => import("@/pages/Training/Training"));
const Departments = lazy(() => import("@/pages/Departments/Departments"));
const Designations = lazy(() => import("@/pages/Designations/Designations"));
const Hierarchy = lazy(() => import("@/pages/Hierarchy/Hierarchy"));
const Reports = lazy(() =>
  import("@/pages/Reports/Reports").then((m) => ({ default: m.Reports || m.default })),
);
const Settings = lazy(() =>
  import("@/pages/Settings/Settings").then((m) => ({ default: m.Settings || m.default })),
);
const MyProfile = lazy(() =>
  import("@/pages/MyProfile/MyProfile").then((m) => ({ default: m.MyProfile || m.default })),
);
const TasksPage = lazy(() => import("@/pages/Tasks"));
const CustomReportBuilder = lazy(() =>
  import("@/pages/Customereportbuilder/customereportbuilder").then((m) => ({ default: m.CustomReportBuilder || m.default })),
);
const ProjectsPage = lazy(() => import("@/pages/Projects/Projects"));
const Roles = lazy(() => import("@/pages/Roles/Roles"));
const Timesheets = lazy(() => import("@/pages/Timesheets/Timesheets"));
const Branches = lazy(() => import("@/pages/Branches/Branches"));
const Companies = lazy(() => import("@/pages/Companies/Companies"));
const CompanyProfile = lazy(() => import("@/pages/Companies/CompanyProfile"));
const Clients = lazy(() => import("@/pages/Clients/Clients"));
const Invoices = lazy(() => import("@/pages/Invoices/Invoices"));
const CompanyDocuments = lazy(
  () => import("@/pages/CompanyDocuments/CompanyDocuments"),
);
const Tracking = lazy(() => import("@/pages/Tracking/Tracking"));
const Shifts = lazy(() => import("@/pages/Shifts/Shifts"));

const ForgotPassword = lazy(() => import("@/pages/Login/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/Login/ResetPassword"));

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100%",
    }}
  >
    <CircularProgress />
  </Box>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Roles & Permissions controls system-wide access, so it must never be
// reachable by a Manager or Employee through a manually entered URL.
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector((s) => s.auth.user);
  const roles = useAppSelector((s) => s.data.roles) || [];
  const rawRole = user?.role;
  const assignedRole = typeof rawRole === "object"
    ? (rawRole as any)?.name
    : roles.find((role: any) => role._id === rawRole || role.id === rawRole)?.name || rawRole;
  const normalizedRole = String(assignedRole || "").trim().toLowerCase();
  const isAdmin = ["admin", "super admin", "master admin"].includes(normalizedRole);

  return isAdmin ? <>{children}</> : <Navigate to="/app/dashboard" replace />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useAppSelector((s) => s.ui.snackbar);
  const users = useAppSelector((s) => s.data.users);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && users.length === 0) {
      dispatch(fetchAllData());
    }
  }, [dispatch, users.length, isAuthenticated]);
  return (
    <>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path={APP_ROUTES.LOGIN} element={<Login />} />
            <Route
              path={APP_ROUTES.FORGOT_PASSWORD}
              element={<ForgotPassword />}
            />
            <Route
              path={APP_ROUTES.RESET_PASSWORD}
              element={<ResetPassword />}
            />
            <Route
              path={APP_ROUTES.APP}
              element={
                <PrivateRoute>
                  <Shell />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="chat" element={<Chat />} />
              <Route path="employees" element={<Employees />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="leave" element={<Leave />} />
              <Route path="payroll" element={<Payroll />} />
              <Route path="recruitment" element={<Recruitment />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route path="performance" element={<Performance />} />
              <Route path="training" element={<Training />} />
              <Route path="departments" element={<Departments />} />
              <Route path="designations" element={<Designations />} />
              <Route path="hierarchy" element={<Hierarchy />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="myprofile" element={<MyProfile />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="roles" element={<AdminOnlyRoute><Roles /></AdminOnlyRoute>} />
              <Route path="timesheets" element={<Timesheets />} />
              <Route path="branches" element={<Branches />} />
              <Route path="companies" element={<Companies />} />
              <Route path="companies/:id" element={<CompanyProfile />} />
              <Route path="mycompany" element={<CompanyProfile />} />
              <Route path="clients" element={<Clients />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="company-documents" element={<CompanyDocuments />} />
              <Route path="tracking" element={<Tracking />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="reports/custom-builder" element={<CustomReportBuilder />} />
            </Route>
            <Route
              path="/"
              element={<Navigate to={APP_ROUTES.DASHBOARD} replace />}
            />
            <Route
              path="*"
              element={<Navigate to={APP_ROUTES.DASHBOARD} replace />}
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => dispatch(hideSnackbar())}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

import { HelmetProvider } from "react-helmet-async";
import { SocketProvider } from "@/context/SocketContext";
import IncomingCall from "@/components/chat/IncomingCall";

const AppContentWrapped: React.FC = () => {
  return (
    <>
      <AppContent />
      <IncomingCall />
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <SocketProvider>
            <AppContentWrapped />
          </SocketProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  </HelmetProvider>
);

export default App;
